import React, { useEffect, useMemo } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import trackingKeys from '@/client/constants/analytics/trackingKeys';
import LinkGenerator from '@/client/components/LinkGenerator/LinkGenerator';
import { Pill, Pills, Icon, Link, PrimaryButton } from '@hz-design-system/web-ui';

import { linkGeneratorHelper, generateLinkObject } from '@/utils/links/linkGeneratorHelper';

import filterChipsHelper from '@/client/utils/filterChipsHelper';
import pushToRouter from '@/client/utils/pushToRouter';
import getClientPageQueryObject from '@/client/utils/getClientPageQueryObject';
import { transformFilterGroups } from '../FilterSidebar/utils/carsFilters';
import { attributeKeys } from '@/constants/attributes/nonStandards';

export type TFilterChipsProps = {
  searchRequestObject: TSearchRequestObject;
  categoriesById: TCategories;
  attributeHierarchy: TAttributeHierarchy;
  facets: TFacet[];
  showMobileFilterDialog?: () => void;
  isMobile: boolean;
};

const FilterChips = ({
  searchRequestObject,
  attributeHierarchy,
  categoriesById,
  facets,
  showMobileFilterDialog,
  isMobile,
}: TFilterChipsProps) => {
  const { t } = useI18nContext();
  const chips = filterChipsHelper(searchRequestObject, categoriesById, facets, t);

  const priceTypeFacet = useMemo(() => {
    const groups = transformFilterGroups(facets) as { basic?: TFacet[] };
    return (groups.basic ?? []).find((f) => f?.key === attributeKeys.PRICE_TYPE);
  }, [facets]);

  useEffect(() => {
    const header = document.querySelector('.hz-Header');
    if (!header) return;

    let pendingAnimationFrameId: number | null = null;

    const observer = new ResizeObserver(() => {
      if (pendingAnimationFrameId !== null) {
        cancelAnimationFrame(pendingAnimationFrameId);
      }
      pendingAnimationFrameId = window.requestAnimationFrame(() => {
        const height = header.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
        pendingAnimationFrameId = null;
      });
    });

    observer.observe(header);

    return () => {
      observer.disconnect();
      if (pendingAnimationFrameId !== null) {
        cancelAnimationFrame(pendingAnimationFrameId);
      }
    };
  }, []);

  const removeAllFiltersNavigation = () => {
    const options = {
      searchRequestObject,
      attributeHierarchy,
      removeAllOtherFilters: true,
      removeAllAttributes: true,
      eventAction: trackingKeys.FILTER_CHIPS.EVENT_ACTION.REMOVE_ALL,
      eventLabel: trackingKeys.FILTER_CHIPS.EVENT_LABEL,
    };

    const href = linkGeneratorHelper(options);
    if (href === '/') {
      window.location.assign('/');
    }
    const query = getClientPageQueryObject(generateLinkObject(options));
    pushToRouter({ query, href });
  };

  return (
    <Pills className="stickyPills">
      <div className="pillsContainer">
        {isMobile && (
          <PrimaryButton
            className={`allFiltersPill ${!chips.length ? 'wideFiltersPill' : ''}`}
            onClick={showMobileFilterDialog}
          >
            <Icon name="filter" color="background-info-soft" />
            {t('filters.allFilters')}
          </PrimaryButton>
        )}
        {Boolean(chips.length) && (
          <div id="main-container" className="chipsSlider">
            <div className="innerWrapper">
              {chips.map((chip) => (
                <div className="pillWrapper" key={chip.label}>
                  <LinkGenerator
                    searchRequestObject={searchRequestObject}
                    {...(chip.linkProps as any)}
                    eventAction={trackingKeys.FILTER_CHIPS.EVENT_ACTION.CHIP_ITEM}
                    eventLabel={trackingKeys.FILTER_CHIPS.EVENT_LABEL}
                    tabIndex={-1}
                    priceTypeFacet={priceTypeFacet}
                    priceTypeIdentifier={chip?.key}
                  >
                    <Pill
                      className="hz-Filter-chip"
                      translations={{
                        ariaLabelDefault: chip.label || '',
                        ariaLabelActive: chip.label || '',
                      }}
                      isCloseable
                      isActive
                      tabIndex={0}
                    >
                      {chip.label}
                    </Pill>
                  </LinkGenerator>
                </div>
              ))}
              {chips.length > 1 && (
                <div className="deletePills">
                  <Link
                    role="button"
                    className="chipsRemoveAll"
                    onClick={removeAllFiltersNavigation}
                    aria-label={`${chips[0]?.label || ''}, ${t('filters.clear')}`}
                  >
                    {t('filters.clear')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Pills>
  );
};

export default FilterChips;
